import { AnimatePresence, motion } from 'framer-motion'
import React, { useMemo, useState } from 'react'
import { useMatch, useResolvedPath } from 'react-router'
import { Link, LinkProps } from 'react-router-dom'
import { ReactComponent as ChevronDown } from '../../images/icons/chevron_down.svg'
import { ReactComponent as ChevronUp } from '../../images/icons/chevron_up.svg'
import { useDealership } from '../../store/zustand/dealershipStore'
import { GA4 } from '../../lib/utils/ga4'
import mtm from '../../lib/utils/mtm'
import classNames from 'classnames'
import { MenuItem } from './LeftBarMenu'

type Props = {
  name: string
  subMenuItems?: Array<MenuItem>
  icon?: JSX.Element
  isSubMenuItem?: boolean
  linkClassName?: string
}

const CustomLink = ({
  children,
  to,
  className,
  linkClassName,
  name,
  icon,
  subMenuItems,
  isSubMenuItem,
  ...props
}: LinkProps & Props): JSX.Element => {
  const resolved = useResolvedPath(to)
  const dealership = useDealership()
  const match = useMatch({ path: resolved.pathname, end: false })

  const agencyId = useMemo(() => {
    return dealership?.organization_id
  }, [dealership?.organization_id])

  const dealershipId = useMemo(() => {
    return dealership?.id
  }, [dealership?.id])

  const [expanded, setExpanded] = useState(false)
  const toggleExpanded = (): void => setExpanded(!expanded)

  if (subMenuItems) {
    return (
      <div className={`font-bold pt-5`}>
        <div
          className={classNames(
            'flex flex-row justify-between items-center cursor-pointer text-white text-xs',
            { 'opacity-50': !match },
          )}
          onClick={toggleExpanded}>
          <div className="flex flex-row items-center justify-center">
            {icon}
            <span className="text-center">{name}</span>
          </div>
          {expanded ? (
            <div className="flex items-center justify-center rounded-full bg-space-cadet">
              <ChevronUp fill={'#FFF'} />
            </div>
          ) : (
            <div className="flex items-center justify-center rounded-full bg-space-cadet">
              <ChevronDown fill={'#FFF'} />
            </div>
          )}
        </div>
        <AnimatePresence initial={false}>
          {expanded && (
            <motion.div
              initial="collapsed"
              exit="collapsed"
              animate={expanded ? 'open' : 'collapsed'}
              variants={{
                open: { opacity: 1, height: 'auto' },
                collapsed: { opacity: 0, height: 0 },
              }}
              transition={{ type: 'spring', mass: 1, damping: 50, stiffness: 500 }}
              className={`pl-7 overflow-hidden ${expanded ? '' : ''}`}>
              {subMenuItems.map(({ name, path, hidden }) => {
                if (hidden) return null
                return (
                  <CustomLink
                    to={path
                      // @ts-ignore
                      .replace(':agencyId', agencyId)
                      // @ts-ignore
                      .replace(':dealerId', dealershipId)}
                    key={path}
                    name={name}
                    isSubMenuItem={true}
                    className={'pt-3'}
                    onClick={() => {
                      GA4.sendEvent({
                        event_name: 'menu_click',
                        event_params: name,
                      })
                      mtm.sendEvent('Click', 'Menu', name)
                    }}
                  />
                )
              })}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    )
  }

  return (
    <div className={className}>
      <Link
        className={classNames(
          `font-bold flex ${linkClassName} text-white text-xs no-underline`,
          { 'opacity-50': !match },
        )}
        to={(to as string)
          .replace(':agencyId', agencyId?.toString() ?? '')
          .replace(':dealerId', dealershipId?.toString() ?? '')}
        {...props}>
        <div className="flex flex-row items-center">
          {icon}
          <span className="text-center">{name}</span>
        </div>
      </Link>
    </div>
  )
}

export default CustomLink
